import { NativeSelect } from "../componentes/NativeSelect"
import { GavetaDaFicha, RodapeDaGaveta } from "../ficha/Gaveta"
import { Textarea } from "../componentes/Textarea"
import { Column } from "../componentes/TablePrincipal2"
import { BotoesDaLinha, COLUNA_DE_ACOES, DetalheDaFicha, SUCESSO, TabelaDaFicha, VazioDaFicha, useLinhasAbertas } from "../ficha/Tabela"
import { BOTAO_CANCELAR } from "../lib/classes"
import { EsqueletoDeTabela } from "../ficha/Pedido"
import { FalhaNaSecao } from "../componentes/Secao"
import { Input } from "../componentes/Input"
import { useState } from "react"

import {
  ErroDaApi,
  api,
  type DeclaracaoNaTela,
  type EventoNaTela,
  type MemoriaNaTela,
  type ProvidenciaNaTela,
  type VigenciaDoContrato,
} from "../api"
import { Button } from "../componentes/Button"
import { CampoDeData } from "../componentes/CampoDeData"
import { Card, CardAction, CardContent, CardHeader, CardTitle } from "../componentes/Card"
import { ConfirmarComSenha, ResumoDoAto } from "../componentes/ConfirmarComSenha"
import * as Icones from "../componentes/Icones"
import {
  Selo,
  SeloDoValor,
  chaveDoNivel,
  type ChaveDeSituacao,
} from "../componentes/Selo"
import { RotuloDoCampo, faltaNoFormulario } from "../componentes/RotuloDoCampo"
import { data, dataHora, maiusculaInicial, plural } from "../formato"
import {
  GRADE_FORMULARIO,
  MAIUSCULA,
  MAIUSCULA_INLINE,
  NOTA,
  RECUSA,
  VAZIO,
} from "../lib/classes"
import { cn } from "../lib/cn"

export type BlocoDaVigencia = "memoria" | "providencias" | "historico"

const NOME_DO_MARCO: Record<string, string> = {
  assinatura: "assinatura",
  publicacao: "publicação",
  ordem_de_servico: "ordem de serviço",
  data_expressa: "data expressa",
}

const NOME_DO_RESPONSAVEL: Record<string, string> = {
  fiscal: "fiscal",
  gestor: "gestor",
  setor_de_contratos: "setor de contratos",
  secretaria: "secretaria",
  administracao: "administração",
}

const NOME_DO_EVENTO: Record<string, string> = {
  elaboracao: "elaboração",
  assinatura: "assinatura",
  celebracao: "celebração",
  publicacao: "publicação",
  pncp: "divulgação no PNCP",
  eficacia: "eficácia",
  ordem_de_servico: "ordem de serviço",
  inicio_execucao: "início da execução",
  recebimento_provisorio: "recebimento provisório",
  recebimento_definitivo: "recebimento definitivo",
}

const TIPOS_DE_EVENTO = Object.keys(NOME_DO_EVENTO)
const CRITICIDADES = ["baixa", "media", "alta", "critica"]
const NOME_DA_CRITICIDADE: Record<string, string> = {
  baixa: "Baixa",
  media: "Média",
  alta: "Alta",
  critica: "Crítica",
}

const TITULO_DO_BLOCO: Record<BlocoDaVigencia, string> = {
  memoria: "Memória de vigência",
  providencias: "Providências de continuidade",
  historico: "Histórico do cálculo",
}

function mensagem(falha: unknown): string {
  if (falha instanceof ErroDaApi)
    return falha.acao !== "" ? `${falha.regra}. ${falha.acao}` : falha.regra
  return falha instanceof Error ? falha.message : "falha"
}

type Props = {
  contratoId: number
  bloco: BlocoDaVigencia
  estado: VigenciaDoContrato | null
  erro: string | null
  aoTentar: () => void
  aoAtualizar: (novo: VigenciaDoContrato) => void
}

export function Vigencia({ contratoId, bloco, estado, erro, aoTentar, aoAtualizar }: Props) {
  const [aviso, definirAviso] = useState<string | null>(null)
  const [ocupado, definirOcupado] = useState(false)
  const [motivo, definirMotivo] = useState("")
  const [novoEvento, definirNovoEvento] = useState({
    tipo: "publicacao",
    data: "",
    fonte: "",
    veiculo: "",
    referencia: "",
  })

  const [pedindoSenha, definirPedindoSenha] = useState<{
    titulo: string
    tom: "azul" | "perigo"
    rotuloDoAto: string
    iconeDoAto: React.ReactNode
    resumo: React.ReactNode
    gravar: (senha: string) => Promise<VigenciaDoContrato>
    depois?: () => void
  } | null>(null)

  const [gavetaDeValidar, definirGavetaDeValidar] = useState(false)
  const [gavetaDeEvento, definirGavetaDeEvento] = useState(false)
  const [agindo, definirAgindo] = useState<{ id: number; ato: "concluida" | "dispensada" } | null>(null)
  const linhasDasProvidencias = useLinhasAbertas<number>()
  const linhasDoHistorico = useLinhasAbertas<string>()
  const linhasDosEventos = useLinhasAbertas<number>()
  const agir = async (acao: () => Promise<VigenciaDoContrato>) => {
    definirOcupado(true)
    definirAviso(null)
    try {
      aoAtualizar(await acao())
      definirMotivo("")
    } catch (falha: unknown) {
      definirAviso(mensagem(falha))
    } finally {
      definirOcupado(false)
    }
  }

  if (erro !== null) {
    return (
      <Cartao titulo={TITULO_DO_BLOCO[bloco]}>
        <FalhaNaSecao mensagem={`Não foi possível carregar a vigência: ${erro}`} aoTentar={aoTentar} />
      </Cartao>
    )
  }
  if (estado === null) {
    return (
      <Cartao titulo={TITULO_DO_BLOCO[bloco]}>
        <EsqueletoDeTabela />
      </Cartao>
    )
  }

  if (bloco === "providencias") {
    return (
      <Cartao titulo={TITULO_DO_BLOCO.providencias}>
        {aviso !== null && <p className={cn(RECUSA, MAIUSCULA)}>{aviso}</p>}
        <div className="my-3 flex flex-wrap items-center gap-2">
          <label
            className="text-(length:--texto-xs) font-[650] tracking-[0.04em] whitespace-nowrap text-muted-foreground uppercase"
            htmlFor="criticidade"
          >
            Criticidade
          </label>
          <NativeSelect
            id="criticidade" classNameDaCaixa="w-auto flex-[0_1_16rem]"
            value={estado.criticidade ?? ""}
            disabled={ocupado || !estado.pode_agir}
            title={estado.pode_agir ? undefined : "Quem define é o fiscal designado ou o gestor"}
            onChange={(e) =>
              void agir(() =>
                api.definirCriticidade(
                  contratoId,
                  e.target.value === "" ? null : e.target.value,
                ),
              )
            }
          >
            <option value="">Não avaliada</option>
            {CRITICIDADES.map((c) => (
              <option key={c} value={c}>
                {NOME_DA_CRITICIDADE[c]}
              </option>
            ))}
          </NativeSelect>
          <span className={NOTA}>Alta e crítica antecipam todas as faixas</span>
        </div>
        <TabelaDaFicha<ProvidenciaNaTela>
          dados={estado.providencias}
          ordenar={{ faixa_dias: (p) => p.faixa_dias, prazo: (p) => p.prazo }}
          vazio={
            <VazioDaFicha
              titulo="Nenhuma providência"
              descricao="O contrato não tem data de término, ou é fornecimento já entregue. Registre a publicação no bloco Memória de vigência e recalcule."
            />
          }
          linhaClicavel={(p) => p.observacao !== null}
          aoClicarNaLinha={(p) => linhasDasProvidencias.alternar(p.id)}
          linhaAberta={(p) => agindo?.id === p.id || linhasDasProvidencias.aberta(p.id)}
          detalheDaLinha={(p) =>
            agindo?.id === p.id ? (
              <FormularioDaProvidencia
                ato={agindo.ato}
                ocupado={ocupado}
                aoCancelar={() => definirAgindo(null)}
                aoConfirmar={(observacao) => {
                  definirAgindo(null)
                  void agir(() => api.mudarProvidencia(contratoId, p.id, agindo.ato, observacao))
                }}
              />
            ) : (
              <DetalheDaFicha rotulo="Observação">{p.observacao}</DetalheDaFicha>
            )
          }
        >
          <Column<ProvidenciaNaTela> name="descricao" label="Providência" render={(_v, p) => <span className={MAIUSCULA}>{p.descricao}</span>} />
          <Column<ProvidenciaNaTela> name="faixa_dias" label="Faixa" render={(_v, p) => <span className="tabular-nums">{plural(p.faixa_dias, "dia", "dias")}</span>} />
          <Column<ProvidenciaNaTela>
            name="prazo"
            label="Prazo"
            render={(_v, p) => (
              <span className="tabular-nums">
                {data(p.prazo)}
                {p.referencia_preventiva && " (preventiva)"}
              </span>
            )}
          />
          <Column<ProvidenciaNaTela>
            name="responsavel"
            label="Responsável"
            render={(_v, p) => (
              <span className={MAIUSCULA}>
                {NOME_DO_RESPONSAVEL[p.responsavel] ?? p.responsavel}
                {p.escalonamento !== null && ` → ${NOME_DO_RESPONSAVEL[p.escalonamento] ?? p.escalonamento}`}
              </span>
            )}
          />
          <Column<ProvidenciaNaTela> name="situacao" label="Situação" render={(_v, p) => <Selo situacao={situacaoDaProvidencia(p)} />} />
          {estado.pode_agir && (
            <Column<ProvidenciaNaTela>
              name="acoes"
              label="Ações"
              classe={COLUNA_DE_ACOES}
              render={(_v, p) => (
                <BotoesDaLinha
                  acoes={
                    p.situacao === "pendente"
                      ? [
                          { rotulo: "Concluir", icone: Icones.Concluir, sucesso: true, aoClicar: () => definirAgindo({ id: p.id, ato: "concluida" }) },
                          { rotulo: "Dispensar", icone: Icones.Dispensar, perigo: true, aoClicar: () => definirAgindo({ id: p.id, ato: "dispensada" }) },
                        ]
                      : [
                          {
                            rotulo: "Reabrir",
                            icone: Icones.Reabrir,
                            aoClicar: () => void agir(() => api.mudarProvidencia(contratoId, p.id, "pendente", null)),
                          },
                        ]
                  }
                />
              )}
            />
          )}
        </TabelaDaFicha>
      </Cartao>
    )
  }

  if (bloco === "historico") {
    return (
      <Cartao titulo={TITULO_DO_BLOCO.historico}>
        <h3 className="mb-3">Datas declaradas</h3>
        <TabelaDaFicha<DeclaracaoNaTela>
          dados={estado.declaracoes}
          ordenar={{ declarada_em: (d) => d.declarada_em }}
          vazio={
            <VazioDaFicha
              titulo="Ninguém corrigiu uma data deste contrato"
              descricao="Quando o fiscal declara a data correta, a declaração fica aqui, com quem, quando e por quê."
            />
          }
          aoClicarNaLinha={(d) => linhasDoHistorico.alternar(`d-${d.id}`)}
          linhaAberta={(d) => linhasDoHistorico.aberta(`d-${d.id}`)}
          detalheDaLinha={(d) => (
            <div className="flex flex-col gap-2">
              <DetalheDaFicha rotulo="Motivo">{d.motivo ?? "Declarada sem motivo escrito"}</DetalheDaFicha>
              {d.situacao === "retirada" && (
                <DetalheDaFicha rotulo="Retirada">
                  {`${d.retirada_por !== null ? `Por ${d.retirada_por}, ` : ""}em ${dataHora(d.retirada_em)}${d.motivo_da_retirada !== null ? `: ${d.motivo_da_retirada}` : ""}`}
                </DetalheDaFicha>
              )}
              {d.divergencia_data !== null && (
                <DetalheDaFicha rotulo="Divergência">
                  {`O sistema encontrou depois ${data(d.divergencia_data)} (${d.divergencia_fonte ?? "sistema"})`}
                </DetalheDaFicha>
              )}
            </div>
          )}
        >
          <Column<DeclaracaoNaTela> name="declarada_em" label="Quando" render={(_v, d) => <span className="tabular-nums">{dataHora(d.declarada_em)}</span>} />
          <Column<DeclaracaoNaTela> name="rotulo" label="Data" render={(_v, d) => <span className={MAIUSCULA}>{d.rotulo}</span>} />
          <Column<DeclaracaoNaTela> name="data_anterior" label="Antes" render={(_v, d) => <span className="tabular-nums">{data(d.data_anterior)}</span>} />
          <Column<DeclaracaoNaTela> name="data" label="Declarada" render={(_v, d) => <span className="tabular-nums">{data(d.data)}</span>} />
          <Column<DeclaracaoNaTela> name="quem" label="Quem" render={(_v, d) => `${d.quem ?? "—"} (${d.papel})`} />
          <Column<DeclaracaoNaTela> name="situacao" label="Situação" render={(_v, d) => <span className={MAIUSCULA}>{d.situacao}</span>} />
        </TabelaDaFicha>
        <h3 className="mt-6 mb-3">Cálculos anteriores</h3>
        <TabelaDaFicha<MemoriaNaTela>
          dados={estado.historico}
          ordenar={{ calculada_em: (m) => m.calculada_em }}
          vazio={<VazioDaFicha titulo="Nenhuma memória anterior" />}
          aoClicarNaLinha={(m) => linhasDoHistorico.alternar(`m-${m.id}`)}
          linhaAberta={(m) => linhasDoHistorico.aberta(`m-${m.id}`)}
          detalheDaLinha={(m) => (
            <div className="flex flex-col gap-2">
              <DetalheDaFicha rotulo="Explicação">{m.explicacao}</DetalheDaFicha>
              <DetalheDaFicha rotulo="Nível">{m.nivel_rotulo}</DetalheDaFicha>
            </div>
          )}
        >
          <Column<MemoriaNaTela> name="calculada_em" label="Calculada em" render={(_v, m) => <span className="tabular-nums">{dataHora(m.calculada_em)}</span>} />
          <Column<MemoriaNaTela> name="regra_rotulo" label="Regra" render={(_v, m) => m.regra_rotulo} />
          <Column<MemoriaNaTela> name="estado" label="Situação" render={(_v, m) => <SeloDoValor valor={m.estado} />} />
          <Column<MemoriaNaTela> name="data_juridica" label="Término" render={(_v, m) => <span className="tabular-nums">{data(m.data_juridica ?? m.data_preventiva)}</span>} />
        </TabelaDaFicha>
      </Cartao>
    )
  }

  const memoria = estado.memoria
  const disputa = estado.pendente_de_decisao

  return (
    <Card>
      <CardHeader>
        <CardTitle>{TITULO_DO_BLOCO.memoria}</CardTitle>
        <CardAction>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {memoria !== null && (
              <Selo situacao={chaveDoNivel(memoria.nivel)} titulo={memoria.nivel_rotulo} />
            )}
            {memoria !== null && <SeloDoValor valor={memoria.estado} />}
            {estado.pode_agir && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={ocupado}
                onClick={() => void agir(() => api.recalcularVigencia(contratoId))}
              >
                <Icones.Recalcular />
                {ocupado ? "Calculando..." : "Recalcular agora"}
              </Button>
            )}
          </div>
        </CardAction>
      </CardHeader>
      <CardContent>
        {aviso !== null && <p className={cn(RECUSA, MAIUSCULA)}>{aviso}</p>}

        {memoria === null ? (
          <p className={cn(VAZIO, MAIUSCULA)}>
            A vigência deste contrato ainda não foi calculada. Use o botão acima
            para calcular agora.
          </p>
        ) : (
          <>
            <div className="mt-[22px] grid grid-cols-1 gap-3 min-[781px]:grid-cols-2 min-[1181px]:grid-cols-4">
              <Dado
                rotulo="Término jurídico"
                valor={data(memoria.data_juridica)}
                destaque
              />
              <Dado
                rotulo="Data preventiva"
                valor={
                  memoria.data_preventiva === null
                    ? "—"
                    : `${data(memoria.data_preventiva)} (preventiva)`
                }
              />
              <Dado rotulo="Data operacional" valor={data(memoria.data_operacional)} />
              <Dado
                rotulo="Regra e marco"
                valor={`${memoria.regra_rotulo}${
                  memoria.marco !== null
                    ? ` · ${NOME_DO_MARCO[memoria.marco] ?? memoria.marco}`
                    : ""
                }`}
              />
            </div>
            <p className={cn("mt-3 text-(length:--texto-sm) leading-(--linha-densa) text-foreground", MAIUSCULA)}>{memoria.explicacao}</p>
            <p className="mt-1 text-(length:--texto-xs) text-muted-foreground">
              {memoria.exige_validacao && "Exige validação humana"}
              {memoria.validado_em !== null && ` · Validado em ${dataHora(memoria.validado_em)}`}
              {memoria.exige_validacao || memoria.validado_em !== null ? " · " : ""}
              {"Calculada em "}
              {dataHora(memoria.calculada_em)}
            </p>
            {(memoria.fonte !== null ||
              memoria.parametro_procedencia !== null ||
              memoria.evidencia !== null ||
              memoria.motivo_da_alteracao !== null) && (
              <dl className="mt-3 flex flex-col gap-2 rounded-(--raio-controle) border border-(--borda) bg-(--fundo) px-4 py-3">
                {memoria.fonte !== null && (
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-(length:--texto-xs) [&>dd]:m-0 [&>dd]:text-muted-foreground [&>dd]:first-letter:uppercase [&>dt]:min-w-[33px] [&>dt]:flex-none [&>dt]:text-foreground">
                    <dt>Fonte:</dt>
                    <dd>{memoria.fonte}</dd>
                  </div>
                )}
                {memoria.parametro_procedencia !== null && (
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-(length:--texto-xs) [&>dd]:m-0 [&>dd]:text-muted-foreground [&>dd]:first-letter:uppercase [&>dt]:min-w-[33px] [&>dt]:flex-none [&>dt]:text-foreground">
                    <dt>De onde vem a regra</dt>
                    <dd>{memoria.parametro_procedencia}</dd>
                  </div>
                )}
                {memoria.evidencia !== null && (
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-(length:--texto-xs) [&>dd]:m-0 [&>dd]:text-muted-foreground [&>dd]:first-letter:uppercase [&>dt]:min-w-[33px] [&>dt]:flex-none [&>dt]:text-foreground">
                    <dt>Evidência</dt>
                    <dd>{memoria.evidencia}</dd>
                  </div>
                )}
                {memoria.motivo_da_alteracao !== null && (
                  <div className="flex flex-wrap gap-x-2 gap-y-1 text-(length:--texto-xs) [&>dd]:m-0 [&>dd]:text-muted-foreground [&>dd]:first-letter:uppercase [&>dt]:min-w-[33px] [&>dt]:flex-none [&>dt]:text-foreground">
                    <dt>Motivo registrado</dt>
                    <dd>{memoria.motivo_da_alteracao}</dd>
                  </div>
                )}
              </dl>
            )}
            {memoria.anomalias !== null && memoria.anomalias.length > 0 && (
              <ul className="mt-3 grid list-none grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-x-4 gap-y-2 p-0">
                {(memoria.anomalias as { codigo: string; descricao: string }[]).map(
                  (a) => (
                    <li key={a.codigo + a.descricao} className="flex items-center gap-2 text-(length:--texto-sm) leading-(--linha-densa) text-foreground">
                      <Selo situacao="critico" titulo="Os documentos trazem datas que não combinam entre si">
                        Problema
                      </Selo>
                      <span className={MAIUSCULA_INLINE}>{a.descricao}</span>
                    </li>
                  ),
                )}
              </ul>
            )}

            {disputa !== null && (
              <div className="mt-[22px] rounded-(--raio-controle) border border-(--vermelho) bg-destructive/10 px-[15px] py-[13px] text-[0.8125rem] [&_p]:mt-1.5">
                <strong>
                  O sistema refez o cálculo e chegou a um resultado diferente
                  do que já tinha sido validado.
                </strong>
                <p>{disputa.explicacao}</p>
                <p>
                  Para adotar o cálculo novo, valide-o com motivo; para manter o
                  que está, conteste-o.
                </p>
              </div>
            )}

            {estado.pode_agir && (
              <div className="my-3 flex justify-end">
                <Button type="button" variant="outline" size="sm" onClick={() => definirGavetaDeValidar(true)}>
                  <Icones.Validar />
                  Validar ou contestar
                </Button>
              </div>
            )}
          </>
        )}

        <div className="mt-6 mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="m-0">Datas registradas no processo</h3>
          {estado.pode_agir && (
            <Button type="button" variant="outline" size="sm" onClick={() => definirGavetaDeEvento(true)}>
              <Icones.Mais />
              Registrar evento
            </Button>
          )}
        </div>
        <TabelaDaFicha<EventoNaTela>
          dados={estado.eventos}
          ordenar={{ data: (e) => e.data }}
          vazio={
            <VazioDaFicha
              titulo="Nenhuma data registrada"
              descricao="A publicação é a que mais falta para calcular a vigência com segurança."
            />
          }
          linhaClicavel={(e) => e.evidencia !== null}
          aoClicarNaLinha={(e) => linhasDosEventos.alternar(e.id)}
          linhaAberta={(e) => linhasDosEventos.aberta(e.id)}
          detalheDaLinha={(e) => <DetalheDaFicha rotulo="Evidência">{e.evidencia}</DetalheDaFicha>}
        >
          <Column<EventoNaTela> name="tipo" label="Evento" render={(_v, e) => <span className={MAIUSCULA}>{NOME_DO_EVENTO[e.tipo] ?? e.tipo}</span>} />
          <Column<EventoNaTela> name="data" label="Data" render={(_v, e) => <span className="tabular-nums">{data(e.data)}</span>} />
          <Column<EventoNaTela>
            name="fonte"
            label="Fonte"
            render={(_v, e) => (
              <span className={MAIUSCULA}>
                {e.fonte ?? "—"}
                {e.veiculo !== null && ` · ${e.veiculo}`}
                {e.referencia !== null && ` · ${e.referencia}`}
                {e.pagina !== null && ` · p. ${e.pagina}`}
              </span>
            )}
          />
          <Column<EventoNaTela> name="origem" label="Origem" render={(_v, e) => <span className={MAIUSCULA}>{e.origem}</span>} />
        </TabelaDaFicha>
        <GavetaDaFicha
          aberta={gavetaDeValidar}
          titulo="Validar ou contestar a vigência"
          aoFechar={() => definirGavetaDeValidar(false)}
        >
          {disputa !== null && (
            <p className="m-0 text-sm text-foreground">
              O sistema refez o cálculo e chegou a um resultado diferente do que já tinha sido validado. Para adotar o cálculo
              novo, valide-o com motivo; para manter o que está, conteste-o.
            </p>
          )}
          <div>
            <RotuloDoCampo htmlFor="vigencia-motivo" obrigatorio>
              Motivo
            </RotuloDoCampo>
            <Textarea id="vigencia-motivo" required value={motivo} onChange={(e) => definirMotivo(e.target.value)} />
          </div>
          <RodapeDaGaveta aoCancelar={() => definirGavetaDeValidar(false)} ocupado={ocupado}>
              <Button
                title={motivo.trim() === "" ? "Falta o motivo" : undefined}
                type="button"
                variant="outline"
                className="border-destructive text-destructive hover:text-destructive"
                disabled={ocupado || motivo.trim() === ""}
                onClick={() =>
                  definirPedindoSenha({
                    titulo: "Contestar a vigência",
                    tom: "perigo",
                    rotuloDoAto: "Contestar",
                    iconeDoAto: <Icones.Contestar />,
                    resumo: <ResumoDoAto itens={[["Motivo", motivo.trim()]]} />,
                    gravar: (senha) =>
                      api.contestarVigencia(contratoId, motivo, disputa !== null ? disputa.id : null, senha),
                    depois: () => {
                      definirMotivo("")
                      definirGavetaDeValidar(false)
                    },
                  })
                }
              >
                <Icones.Contestar />
                Contestar
              </Button>
              <Button
                title={motivo.trim() === "" ? "Falta o motivo" : undefined}
                type="button"
                disabled={ocupado || motivo.trim() === ""}
                onClick={() =>
                  definirPedindoSenha({
                    titulo: disputa !== null ? "Adotar o cálculo novo" : "Validar a vigência",
                    tom: "azul",
                    rotuloDoAto: disputa !== null ? "Adotar o cálculo novo" : "Validar",
                    iconeDoAto: <Icones.Validar />,
                    resumo: <ResumoDoAto itens={[["Motivo", motivo.trim()]]} />,
                    gravar: (senha) =>
                      api.validarVigencia(contratoId, motivo, disputa !== null ? disputa.id : null, senha),
                    depois: () => {
                      definirMotivo("")
                      definirGavetaDeValidar(false)
                    },
                  })
                }
              >
                <Icones.Validar />
                {disputa !== null ? "Adotar o cálculo novo" : "Validar"}
              </Button>
          </RodapeDaGaveta>
        </GavetaDaFicha>
        <GavetaDaFicha aberta={gavetaDeEvento} titulo="Registrar evento" aoFechar={() => definirGavetaDeEvento(false)}>
          <div className={GRADE_FORMULARIO}>
          <div>
            <RotuloDoCampo htmlFor="evento-tipo" obrigatorio>
              Tipo de evento
            </RotuloDoCampo>
            <NativeSelect
              id="evento-tipo"
              required
              value={novoEvento.tipo}
              onChange={(e) => definirNovoEvento({ ...novoEvento, tipo: e.target.value })}
            >
              {TIPOS_DE_EVENTO.map((t) => (
                <option key={t} value={t}>
                  {maiusculaInicial(NOME_DO_EVENTO[t] ?? t)}
                </option>
              ))}
            </NativeSelect>
          </div>
          <div>
            <RotuloDoCampo htmlFor="evento-data" obrigatorio>
              Data
            </RotuloDoCampo>
            <CampoDeData
              id="evento-data"
              obrigatorio
              valor={novoEvento.data}
              aoMudar={(valor) => definirNovoEvento({ ...novoEvento, data: valor })}
            />
          </div>
          <div className="col-span-full">
            <RotuloDoCampo htmlFor="evento-fonte" obrigatorio>
              Fonte
            </RotuloDoCampo>
            <Input
              id="evento-fonte"
              required
              placeholder="Diário, nº, página; peça do processo"
              value={novoEvento.fonte}
              onChange={(e) => definirNovoEvento({ ...novoEvento, fonte: e.target.value })}
            />
          </div>
          {novoEvento.tipo === "publicacao" && (
            <>
              <div>
                <RotuloDoCampo htmlFor="evento-veiculo" obrigatorio>
                  Veículo
                </RotuloDoCampo>
                <Input
                  id="evento-veiculo"
                  required
                  placeholder="AROM, diário do município, DOU, PNCP"
                  value={novoEvento.veiculo}
                  onChange={(e) =>
                    definirNovoEvento({ ...novoEvento, veiculo: e.target.value })
                  }
                />
              </div>
              <div>
                <RotuloDoCampo htmlFor="evento-referencia" obrigatorio>
                  Referência
                </RotuloDoCampo>
                <Input
                  id="evento-referencia"
                  required
                  placeholder="Edição, código identificador, página"
                  value={novoEvento.referencia}
                  onChange={(e) =>
                    definirNovoEvento({ ...novoEvento, referencia: e.target.value })
                  }
                />
              </div>
            </>
          )}
          </div>
          <RodapeDaGaveta aoCancelar={() => definirGavetaDeEvento(false)} ocupado={ocupado}>
            <Button
              title={
                faltaNoFormulario([
                  ["a data", novoEvento.data],
                  ["a fonte", novoEvento.fonte],
                  ...(novoEvento.tipo === "publicacao"
                    ? ([
                        ["o veículo", novoEvento.veiculo],
                        ["a referência", novoEvento.referencia],
                      ] as [string, string][])
                    : []),
                ]) ?? undefined
              }
              type="button"
              disabled={
                ocupado ||
                novoEvento.data === "" ||
                novoEvento.fonte.trim() === "" ||
                (novoEvento.tipo === "publicacao" &&
                  (novoEvento.veiculo.trim() === "" ||
                    novoEvento.referencia.trim() === ""))
              }
              onClick={() =>
                definirPedindoSenha({
                  titulo: "Registrar o evento",
                  tom: "azul",
                  rotuloDoAto: "Registrar evento",
                  iconeDoAto: <Icones.Enviar />,
                  resumo: (
                    <ResumoDoAto
                      itens={[
                        ["Evento", NOME_DO_EVENTO[novoEvento.tipo] ?? novoEvento.tipo],
                        ["Data", data(novoEvento.data)],
                        ["Fonte", novoEvento.fonte.trim()],
                      ]}
                    />
                  ),
                  depois: () => definirGavetaDeEvento(false),
                  gravar: (senha) =>
                    api.registrarEvento(
                      contratoId,
                      {
                        tipo: novoEvento.tipo,
                        data: novoEvento.data,
                        fonte: novoEvento.fonte,
                        veiculo:
                          novoEvento.veiculo.trim() === "" ? null : novoEvento.veiculo,
                        referencia:
                          novoEvento.referencia.trim() === ""
                            ? null
                            : novoEvento.referencia,
                      },
                      senha,
                    ),
                })
              }
            >
              <Icones.Enviar />
              Registrar evento
            </Button>
          </RodapeDaGaveta>
        </GavetaDaFicha>
        <ConfirmarComSenha
          aberto={pedindoSenha !== null}
          titulo={pedindoSenha?.titulo ?? ""}
          tom={pedindoSenha?.tom}
          resumo={pedindoSenha?.resumo}
          rotuloDoAto={pedindoSenha?.rotuloDoAto ?? ""}
          iconeDoAto={pedindoSenha?.iconeDoAto}
          aoConfirmar={async (senha) => {
            if (pedindoSenha === null) return
            aoAtualizar(await pedindoSenha.gravar(senha))
            definirAviso(null)
            pedindoSenha.depois?.()
          }}
          aoFechar={() => definirPedindoSenha(null)}
        />
      </CardContent>
    </Card>
  )
}

function Cartao({
  titulo,
  acao,
  children,
}: {
  titulo: string
  acao?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{titulo}</CardTitle>
        {acao !== undefined && <CardAction>{acao}</CardAction>}
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  )
}

function Dado({
  rotulo,
  valor,
  destaque = false,
}: {
  rotulo: string
  valor: string
  destaque?: boolean
}) {
  return (
    <div>
      <div className="text-(length:--texto-xs) tracking-[0.08em] text-(--marca-forte) uppercase">{rotulo}</div>
      <div className={cn("mt-[5px] text-[0.9375rem] tabular-nums", destaque && "text-(length:--texto-xl) font-[650]")}>
        {valor}
      </div>
    </div>
  )
}

function situacaoDaProvidencia(providencia: ProvidenciaNaTela): ChaveDeSituacao {
  if (providencia.atrasada) return "atrasada"
  if (providencia.situacao === "concluida") return "concluida"
  if (providencia.situacao === "dispensada") return "dispensada"
  return "pendente"
}

function FormularioDaProvidencia({
  ato,
  ocupado,
  aoCancelar,
  aoConfirmar,
}: {
  ato: "concluida" | "dispensada"
  ocupado: boolean
  aoCancelar: () => void
  aoConfirmar: (observacao: string | null) => void
}) {
  const [observacao, definirObservacao] = useState("")
  const falta = ato === "dispensada" && observacao.trim() === "" ? "Falta a observação" : null
  return (
    <div data-slot="formulario-da-providencia" className="flex flex-col gap-2">
      <Input
        aria-label="Observação da providência"
        placeholder={ato === "dispensada" ? "Por que dispensar" : "Observação (opcional)"}
        value={observacao}
        onChange={(e) => definirObservacao(e.target.value)}
      />
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="outline" size="sm" className={BOTAO_CANCELAR} disabled={ocupado} onClick={aoCancelar}>
          <Icones.Fechar />
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          className={ato === "concluida" ? SUCESSO : undefined}
          disabled={ocupado || falta !== null}
          title={falta ?? undefined}
          onClick={() => aoConfirmar(observacao.trim() === "" ? null : observacao.trim())}
        >
          {ato === "concluida" ? <Icones.Concluir /> : <Icones.Dispensar />}
          {ato === "concluida" ? "Concluir" : "Dispensar"}
        </Button>
      </div>
    </div>
  )
}
